<template>
  <div id="app" :class="{ 'dark-mode': isDarkMode }">
    <header class="app-header">
      <router-link to="/" class="logo">🍳 My Recipes</router-link>
      <div class="header-controls">
        <button 
          v-if="isAdmin" 
          class="admin-btn active"
          @click="logout"
          title="Logout from admin"
        >
          Admin ✓
        </button>
        <button 
          v-else 
          class="admin-btn"
          @click="openLoginModal"
          title="Admin login"
        >
          Admin
        </button>
        <button class="theme-toggle" @click="toggleDarkMode" :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'">
          {{ isDarkMode ? '☀️' : '🌙' }}
        </button>
      </div>
    </header>
    <main>
      <router-view :is-admin="isAdmin" />
    </main>
    
    <!-- Login Modal -->
    <div v-if="showLoginModal" class="modal-overlay" @click.self="showLoginModal = false">
      <div class="modal">
        <h2>Admin Login</h2>
        <form @submit.prevent="login">
          <input 
            type="password" 
            v-model="password" 
            placeholder="Enter admin key"
            class="password-input"
            ref="passwordInput"
            autocomplete="current-password"
            :disabled="loggingIn"
          />
          <p v-if="loginError" class="login-error">{{ loginError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn-cancel" @click="showLoginModal = false" :disabled="loggingIn">Cancel</button>
            <button type="submit" class="btn-login" :disabled="loggingIn">{{ loggingIn ? 'Verifying…' : 'Login' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      isDarkMode: false,
      isAdmin: false,
      showLoginModal: false,
      password: '',
      loginError: '',
      loggingIn: false,
    };
  },
  mounted() {
    // Check for saved preference or system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      this.isDarkMode = savedTheme === 'dark';
    } else {
      this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    
    // Restore saved admin session
    const adminSession = localStorage.getItem('adminSession');
    if (adminSession === 'true' && localStorage.getItem('adminKey')) {
      this.isAdmin = true;
    }
  },
  watch: {
    showLoginModal(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.passwordInput?.focus();
        });
      }
    }
  },
  methods: {
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
    },
    
    openLoginModal() {
      this.loginError = '';
      this.showLoginModal = true;
    },
    
    async login() {
      this.loginError = '';
      this.loggingIn = true;
      try {
        // The key is validated against the api-keys stored on the server.
        await this.$http.post('/validate', { password: this.password });
        
        localStorage.setItem('adminKey', this.password);
        localStorage.setItem('adminSession', 'true');
        this.isAdmin = true;
        this.showLoginModal = false;
        this.password = '';
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.loginError = 'Incorrect admin key';
        } else {
          this.loginError = 'Could not reach the server. Please try again.';
        }
        this.password = '';
      } finally {
        this.loggingIn = false;
      }
    },
    
    logout() {
      this.isAdmin = false;
      localStorage.removeItem('adminSession');
    },
  },
};
</script>

<style>
html, body {
  height: auto;
  min-height: 100%;
  margin: 0;
  padding: 0;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  min-height: 100vh;
  transition: background-color 0.3s, color 0.3s;
}

/* Light mode (default) */
#app {
  --bg-color: #f5f5f7;
  --bg-color-secondary: #ffffff;
  --text-color: #1d1d1f;
  --text-muted: #86868b;
  --accent-color: #0071e3;
  --accent-hover: #0077ed;
  --card-bg: #ffffff;
  --input-bg: #e8e8ed;
  --border-color: #d2d2d7;
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background-color: var(--bg-color);
  color: var(--text-color);
}

/* Dark mode - refined dark theme */
#app.dark-mode {
  --bg-color: #1a1a1c;
  --bg-color-secondary: #242427;
  --text-color: #f5f5f7;
  --text-muted: #a1a1a6;
  --accent-color: #0a84ff;
  --accent-hover: #409cff;
  --card-bg: #242427;
  --input-bg: #2f2f33;
  --border-color: #3a3a3d;
  --shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  background-color: var(--bg-color);
  color: var(--text-color);
}

body {
  background-color: var(--bg-color, #f5f5f7);
  transition: background-color 0.3s;
}

/* App Header */
.app-header {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1em 1.5em;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 1em;
}

.logo {
  font-size: 1.5em;
  font-weight: bold;
  text-decoration: none;
  color: var(--text-color);
}

.logo:hover {
  color: var(--accent-color);
}

.theme-toggle {
  background: var(--input-bg);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  font-size: 1.3em;
  cursor: pointer;
  transition: transform 0.2s, background-color 0.3s;
}

.theme-toggle:hover {
  transform: scale(1.1);
}

main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5em 2em;
}

/* Header controls */
.header-controls {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.admin-btn {
  padding: 0.5em 1em;
  background: var(--input-bg);
  border: 1px solid var(--border-color);
  border-radius: 0.5em;
  cursor: pointer;
  font-size: 0.9em;
  color: var(--text-color);
  transition: all 0.2s;
}

.admin-btn:hover {
  border-color: var(--accent-color);
  color: var(--accent-color);
}

.admin-btn.active {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--card-bg, #fff);
  padding: 2em;
  border-radius: 1em;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--shadow);
}

.modal h2 {
  margin: 0 0 1em;
  color: var(--text-color);
}

.setup-info {
  color: var(--text-muted);
  font-size: 0.9em;
  margin: -0.5em 0 1em;
}

.password-input {
  width: 100%;
  padding: 0.75em 1em;
  border: 1px solid var(--border-color);
  border-radius: 0.5em;
  font-size: 1em;
  background: var(--input-bg);
  color: var(--text-color);
  box-sizing: border-box;
}

.password-input:focus {
  outline: none;
  border-color: var(--accent-color);
}

.login-error {
  color: #e53935;
  margin: 0.5em 0;
  font-size: 0.9em;
}

.modal-actions {
  display: flex;
  gap: 0.5em;
  justify-content: flex-end;
  margin-top: 1.5em;
}

.btn-cancel, .btn-login {
  padding: 0.6em 1.2em;
  border-radius: 0.5em;
  cursor: pointer;
  font-size: 1em;
  transition: all 0.2s;
}

.btn-cancel {
  background: var(--input-bg);
  border: 1px solid var(--border-color);
  color: var(--text-color);
}

.btn-cancel:hover {
  background: var(--border-color);
}

.btn-login {
  background: var(--accent-color);
  border: none;
  color: white;
}

.btn-login:hover {
  opacity: 0.9;
}
</style>
